import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const IntegrationsTab = () => {
  const [integrations, setIntegrations] = useState([
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Connect WebForms with 5,000+ apps through Zapier',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_127a9f142-1764636340877.png",
    logoAlt: 'Zapier orange lightning bolt logo on white background',
    category: 'Integrations',
    status: 'connected',
    connectedDate: '2025-11-15',
    features: ['Form submission triggers', 'App connections', 'Custom workflows']
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Accept payments directly through your forms with secure processing',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_158d6b7b9-1766478566255.png",
    logoAlt: 'Stripe purple and white logo representing payment processing platform',
    category: 'Payments',
    status: 'connected',
    connectedDate: '2025-09-20',
    features: ['One-time payments', 'Subscriptions', 'Multiple currencies']
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'Automatically add form respondents to your email marketing lists',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_140ff6bc9-1764818295096.png",
    logoAlt: 'Mailchimp yellow and black monkey mascot logo representing email marketing platform',
    category: 'Marketing',
    status: 'disconnected',
    connectedDate: null,
    features: ['Auto-add to lists', 'Tag subscribers', 'Segment audiences']
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Sync form submissions with your CRM for better lead management',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_16325c916-1764649041418.png",
    logoAlt: 'HubSpot orange sprocket logo representing CRM and marketing platform',
    category: 'CRM',
    status: 'disconnected',
    connectedDate: null,
    features: ['Create contacts', 'Update deals', 'Trigger workflows']
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Get instant notifications in Slack when forms are submitted',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_103aa447c-1764658017289.png",
    logoAlt: 'Slack colorful hashtag logo representing team communication platform',
    category: 'Communication',
    status: 'connected',
    connectedDate: '2026-01-10',
    features: ['Channel notifications', 'Direct messages', 'Custom formatting']
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    description: 'Automatically export form responses to Google Sheets spreadsheets',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1bdb1acd7-1764748487531.png",
    logoAlt: 'Google Sheets green spreadsheet icon representing cloud spreadsheet platform',
    category: 'Productivity',
    status: 'disconnected',
    connectedDate: null,
    features: ['Real-time sync', 'Custom mapping', 'Multiple sheets']
  }]
  );

  const webhooks = [
  {
    id: 1,
    name: 'Production API',
    url: 'https://api.example.com/webhooks/webforms',
    events: ['form.submitted', 'form.updated'],
    status: 'active',
    lastTriggered: '2026-01-30 05:23:15'
  },
  {
    id: 2,
    name: 'Development Server',
    url: 'https://dev.example.com/webhooks/test',
    events: ['form.submitted'],
    status: 'inactive',
    lastTriggered: '2026-01-28 14:45:30'
  }];


  const apiKeys = [
  {
    id: 1,
    name: 'Production Key',
    key: 'fc_live_••••••••••••••••••••1234',
    created: '2025-06-15',
    lastUsed: '2026-01-30',
    permissions: ['read', 'write']
  },
  {
    id: 2,
    name: 'Development Key',
    key: 'fc_test_••••••••••••••••••••5678',
    created: '2025-12-01',
    lastUsed: '2026-01-29',
    permissions: ['read']
  }];


  const toggleIntegration = (id) => {
    setIntegrations((prev) => prev?.map((integration) => {
      if (integration?.id === id) {
        return {
          ...integration,
          status: integration?.status === 'connected' ? 'disconnected' : 'connected',
          connectedDate: integration?.status === 'connected' ? null : new Date()?.toISOString()?.split('T')?.[0]
        };
      }
      return integration;
    }));
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              Integrations
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
                            Connect WebForms with your favorite tools and services
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {integrations?.map((integration) =>
          <div
            key={integration?.id}
            className="bg-muted/50 rounded-lg p-4 md:p-6 hover:bg-muted transition-smooth">

              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden border border-border flex-shrink-0">
                  <Image
                  src={integration?.logo}
                  alt={integration?.logoAlt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                      {integration?.name}
                    </h3>
                    {integration?.status === 'connected' &&
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded">
                        <Icon name="CheckCircle2" size={12} />
                        Connected
                      </span>
                  }
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {integration?.category}
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                {integration?.description}
              </p>

              <ul className="space-y-2 mb-4">
                {integration?.features?.map((feature, index) =>
              <li key={index} className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
              )}
              </ul>

              {integration?.connectedDate &&
            <p className="text-xs text-muted-foreground mb-4">
                  Connected on {new Date(integration.connectedDate)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
            }

              <div className="flex items-center gap-2">
                <Button
                variant={integration?.status === 'connected' ? 'outline' : 'default'}
                size="default"
                fullWidth
                onClick={() => toggleIntegration(integration?.id)}
                iconName={integration?.status === 'connected' ? 'Unplug' : 'Plug'}
                iconPosition="left">

                  {integration?.status === 'connected' ? 'Disconnect' : 'Connect'}
                </Button>
                {integration?.status === 'connected' &&
              <Button variant="ghost" size="default" iconName="Settings" />
              }
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              Webhooks
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Send real-time data to your custom endpoints
            </p>
          </div>
          <Button
            variant="default"
            size="default"
            iconName="Plus"
            iconPosition="left">

            Add Webhook
          </Button>
        </div>

        <div className="space-y-4">
          {webhooks?.map((webhook) =>
          <div
            key={webhook?.id}
            className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 bg-muted/50 rounded-lg">

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm md:text-base font-medium text-foreground">
                    {webhook?.name}
                  </h3>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                webhook?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted-foreground/10 text-muted-foreground'}`
                }>
                    {webhook?.status}
                  </span>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mb-2 break-all">
                  {webhook?.url}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {webhook?.events?.map((event, index) =>
                <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                      {event}
                    </span>
                )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Last triggered: {webhook?.lastTriggered}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" iconName="Edit2" />
                <Button variant="ghost" size="sm" iconName="Trash2" />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
              API Keys
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Manage your API keys for programmatic access
            </p>
          </div>
          <Button
            variant="default"
            size="default"
            iconName="Key"
            iconPosition="left">

            Generate New Key
          </Button>
        </div>

        <div className="space-y-4">
          {apiKeys?.map((apiKey) =>
          <div
            key={apiKey?.id}
            className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 bg-muted/50 rounded-lg">

              <div className="flex-1 min-w-0">
                <h3 className="text-sm md:text-base font-medium text-foreground mb-2">
                  {apiKey?.name}
                </h3>
                <div className="flex items-center gap-2 mb-2">
                  <code className="px-3 py-1.5 bg-background border border-border rounded text-xs font-mono text-foreground">
                    {apiKey?.key}
                  </code>
                  <Button variant="ghost" size="sm" iconName="Copy" />
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  <span>Created: {new Date(apiKey.created)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <span>Last used: {new Date(apiKey.lastUsed)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <div className="flex items-center gap-1">
                    <span>Permissions:</span>
                    {apiKey?.permissions?.map((perm, index) =>
                  <span key={index} className="px-2 py-0.5 bg-primary/10 text-primary rounded">
                        {perm}
                      </span>
                  )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" iconName="RefreshCw" />
                <Button variant="ghost" size="sm" iconName="Trash2" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);

};

export default IntegrationsTab;